import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Eye, ExternalLink, ChevronDown, ChevronUp, Calendar, Film, FileText, CheckSquare, PlusCircle, Edit2, Trash2, ArrowLeft, Save, X } from 'lucide-react';

const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

const CourseAdminDashboard = () => {
    const { contentId } = useParams();
    const navigate = useNavigate();
    
    const [course, setCourse] = useState(null);
    const [weeks, setWeeks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);
    
    // Editing states
    const [editingWeek, setEditingWeek] = useState(null);
    const [showWeekForm, setShowWeekForm] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null); 
    const [editingNote, setEditingNote] = useState(null);
    const [editingTest, setEditingTest] = useState(null);
    const [expandedWeeks, setExpandedWeeks] = useState({});
    const [weekContent, setWeekContent] = useState({});
    const [availableTests, setAvailableTests] = useState([]);
    const [useOriginalTitle, setUseOriginalTitle] = useState(true);

    // Form states
    const [weekForm, setWeekForm] = useState({ title: '', week_number: '' });
    const [videoForm, setVideoForm] = useState({ title: '', video_link: '', week_id: '' });
    const [noteForm, setNoteForm] = useState({ title: '', file_link: '', week_id: '' });
    const [testForm, setTestForm] = useState({ title: '', test_id: '', week_id: '', start_time: '', end_time: '' });

    const formatForDateTimeInput = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      return local.toISOString().slice(0, 16);
    };
  
    // Notification helper
    const showNotification = (message, type = 'success') => {
      setNotification({ message, type });
      setTimeout(() => setNotification(null), 3000);
    };
  
    // Fetch course details
    useEffect(() => {
      if (contentId) {
        fetchCourseDetails();
        fetchWeeks();
      }
    }, [contentId]);
    useEffect(() => {
        fetch('https://apissc.sainikschoolcadet.com/api/test-type-assignments/type/COURSE_TEST')
          .then(res => res.json())
          .then(async (assignments) => {
            const testPromises = assignments.map(a => 
              fetch(`https://apissc.sainikschoolcadet.com/api/tests/${a.test_id}`)
                .then(res => res.json())
            );
            const tests = await Promise.all(testPromises);
            setAvailableTests(tests); // [{ test_id, name }, ...]
          });
      }, []);
    const fetchCourseDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/Contents/${contentId}`);
        const data = await response.json();
        setCourse(data);
      } catch (error) {
        console.error('Error fetching course details:', error);
        showNotification('Error loading course details', 'error');
      }
    };
  
    const fetchWeeks = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-weeks/recordedClass/${contentId}`);
        const data = await response.json();
        setWeeks(data);
        
        // Initialize expanded state
        const initialExpandedState = {};
        data.forEach(week => {
          initialExpandedState[week.id] = false; // Start with all weeks expanded for admin view
          fetchWeekContent(week.id);
        });
        setExpandedWeeks(initialExpandedState);
      } catch (error) {
        console.error('Error fetching weeks:', error);
        showNotification('Error loading weeks', 'error');
      } finally {
        setLoading(false);
      }
    };
  
    const fetchWeekContent = async (weekId) => {
      try {
        const [videosResponse, notesResponse, testsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/recorded-class-videos?week_id=${weekId}`),
          fetch(`${API_BASE_URL}/recorded-class-notes?week_id=${weekId}`),
          fetch(`${API_BASE_URL}/recorded-class-tests?week_id=${weekId}`)
        ]);
  
        const [videos, notes, tests] = await Promise.all([
          videosResponse.json(),
          notesResponse.json(),
          testsResponse.json()
        ]);
  
        setWeekContent(prev => ({
          ...prev,
          [weekId]: { videos, notes, tests }
        }));
      } catch (error) {
        console.error(`Error fetching content for week ${weekId}:`, error);
      }
    };
  
    const toggleWeek = (weekId) => {
        setExpandedWeeks(prev => {
          const isExpanding = !prev[weekId];
      
          if (isExpanding && !weekContent[weekId]) {
            fetchWeekContent(weekId); // Load data only when expanding for the first time
          }
      
          return {
            ...prev,
            [weekId]: isExpanding
          };
        });
      };
    // CRUD Operations for Weeks
    const handleAddWeek = async (e) => {
      e.preventDefault();
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-weeks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recorded_content_id: contentId, // Match DB field name
            week_number: weekForm.week_number,
            title: weekForm.title
          })
        });
        
        if (response.ok) {
          showNotification('Week added successfully');
          fetchWeeks();
          setShowWeekForm(false);
          setWeekForm({ title: '', week_number: '' });
        } else {
          showNotification('Failed to add week', 'error');
        }
      } catch (error) {
        console.error('Error adding week:', error);
        showNotification('Error adding week', 'error');
      }
    };
  
    const handleUpdateWeek = async (e) => {
      e.preventDefault();
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-weeks/${editingWeek}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recorded_content_id: contentId, // Match DB field name
            week_number: weekForm.week_number,
            title: weekForm.title
          })
        });
        
        if (response.ok) {
          showNotification('Week updated successfully');
          fetchWeeks();
          setEditingWeek(null);
          setWeekForm({ title: '', week_number: '' });
        } else {
          showNotification('Failed to update week', 'error');
        }
      } catch (error) {
        console.error('Error updating week:', error);
        showNotification('Error updating week', 'error');
      }
    };
  
    const handleDeleteWeek = async (weekId) => {
      if (!window.confirm('Are you sure you want to delete this week and all its content?')) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-weeks/${weekId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          showNotification('Week deleted successfully');
          fetchWeeks();
        } else {
          showNotification('Failed to delete week', 'error');
        }
      } catch (error) {
        console.error('Error deleting week:', error);
        showNotification('Error deleting week', 'error');
      }
    };
  
    // CRUD Operations for Videos
    const handleAddVideo = async (e, weekId) => {
  e.preventDefault();
  
  // For debugging - check what data you're sending
  console.log('Adding video with data:', {
    week_id: weekId,
    title: videoForm.title,
    video_link: videoForm.video_link
  });
  
  try {
    const response = await fetch('https://apissc.sainikschoolcadet.com/api/recorded-class-videos', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        // Add any auth headers if needed
      },
      body: JSON.stringify({
        week_id: weekId,
        title: videoForm.title,
        video_link: videoForm.video_link
      })
    });
    
    const responseData = await response.json();
    console.log('API Response:', responseData);
    
    if (response.ok) {
      alert('Video added successfully!');
      // Reset form
      setVideoForm({ title: '', video_link: '', week_id: '' });
      // Close form
      setEditingVideo(null);
      // Refresh video list
      fetchWeekContent(weekId);
    } else {
      alert(`Failed to add video: ${responseData.error || 'Unknown error'}`);
    }
  } catch (error) {
    console.error('Error adding video:', error);
    alert(`Error adding video: ${error.message}`);
  }
};
    const handleUpdateVideo = async (e, weekId) => {
      e.preventDefault();
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-videos/${editingVideo}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            week_id: weekId,
            title: videoForm.title,
            video_link: videoForm.video_link // Match DB field
          })
        });
        
        if (response.ok) {
          showNotification('Video updated successfully');
          fetchWeekContent(weekId);
          setEditingVideo(null);
          setVideoForm({ title: '', video_link: '', week_id: '' });
        } else {
          showNotification('Failed to update video', 'error');
        }
      } catch (error) {
        console.error('Error updating video:', error);
        showNotification('Error updating video', 'error');
      }
    };
  
    const handleDeleteVideo = async (videoId, weekId) => {
      if (!window.confirm('Are you sure you want to delete this video?')) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-videos/${videoId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          showNotification('Video deleted successfully');
          fetchWeekContent(weekId);
        } else {
          showNotification('Failed to delete video', 'error');
        }
      } catch (error) {
        console.error('Error deleting video:', error);
        showNotification('Error deleting video', 'error');
      }
    };
  
    // CRUD Operations for Notes
    const handleAddNote = async (e) => {
        e.preventDefault();
      
        const { title, week_id, file } = noteForm;
      
        if (!title.trim() || !file || !week_id) {
          showNotification('Please fill in Title, File, and Week ID', 'error');
          return;
        }
      
        try {
          const formData = new FormData();
          formData.append('title', title);
          formData.append('week_id', week_id);
          formData.append('file', file); // file from file input
      
          const response = await fetch(`${API_BASE_URL}/recorded-class-notes`, {
            method: 'POST',
            body: formData, // Do not set Content-Type manually
          });
      
          if (response.ok) {
            showNotification('Note added successfully');
            fetchWeekContent(week_id);
            setEditingNote(null);
            setNoteForm({ title: '', week_id: '', file: null });
          } else {
            const errorData = await response.json();
            console.error('Server error:', errorData);
            showNotification(errorData.message || 'Failed to add note', 'error');
          }
        } catch (error) {
          console.error('Error adding note:', error);
          showNotification('Error adding note', 'error');
        }
      };
      const handleUpdateNote = async (e, weekId) => {
        e.preventDefault();
      
        try {
          const formData = new FormData();
          formData.append('week_id', weekId);
          formData.append('title', noteForm.title);
          if (noteForm.file) {
            formData.append('file', noteForm.file); // Only if user uploaded new file
          }
      
          const response = await fetch(`${API_BASE_URL}/recorded-class-notes/${editingNote}`, {
            method: 'PUT',
            body: formData,
          });
      
          if (response.ok) {
            showNotification('Note updated successfully');
            fetchWeekContent(weekId);
            setEditingNote(null);
            setNoteForm({ title: '', week_id: '', file: null });
          } else {
            showNotification('Failed to update note', 'error');
          }
        } catch (error) {
          console.error('Error updating note:', error);
          showNotification('Error updating note', 'error');
        }
      };
            
  
    const handleDeleteNote = async (noteId, weekId) => {
      if (!window.confirm('Are you sure you want to delete this note?')) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-notes/${noteId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          showNotification('Note deleted successfully');
          fetchWeekContent(weekId);
        } else {
          showNotification('Failed to delete note', 'error');
        }
      } catch (error) {
        console.error('Error deleting note:', error);
        showNotification('Error deleting note', 'error');
      }
    };
  
    // CRUD Operations for Tests
    const handleAddTest = async (e, weekId) => {
      e.preventDefault();
      if (testForm.start_time && testForm.end_time && new Date(testForm.end_time) <= new Date(testForm.start_time)) {
        showNotification('End time must be after start time', 'error');
        return;
      }
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-tests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            week_id: weekId,
            test_id: testForm.test_id,
            title: testForm.title,
            start_time: testForm.start_time ? new Date(testForm.start_time).toISOString() : null,
            end_time: testForm.end_time ? new Date(testForm.end_time).toISOString() : null
          })
        });
        
        if (response.ok) {
          showNotification('Test added successfully');
          fetchWeekContent(weekId);
          setEditingTest(null);
          setTestForm({ title: '', test_id: '', week_id: '', start_time: '', end_time: '' });
        } else {
          showNotification('Failed to add test', 'error');
        }
      } catch (error) {
        console.error('Error adding test:', error);
        showNotification('Error adding test', 'error');
      }
    };
  
    const handleUpdateTest = async (e, weekId) => {
      e.preventDefault();
      if (testForm.start_time && testForm.end_time && new Date(testForm.end_time) <= new Date(testForm.start_time)) {
        showNotification('End time must be after start time', 'error');
        return;
      }
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-tests/${editingTest}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            week_id: weekId,
            test_id: testForm.test_id,
            title: testForm.title,
            start_time: testForm.start_time ? new Date(testForm.start_time).toISOString() : null,
            end_time: testForm.end_time ? new Date(testForm.end_time).toISOString() : null
          })
        });
        
        if (response.ok) {
          showNotification('Test updated successfully');
          fetchWeekContent(weekId);
          setEditingTest(null);
          setTestForm({ title: '', test_id: '', week_id: '', start_time: '', end_time: '' });
        } else {
          showNotification('Failed to update test', 'error');
        }
      } catch (error) {
        console.error('Error updating test:', error);
        showNotification('Error updating test', 'error');
      }
    };
  
    const handleDeleteTest = async (testId, weekId) => {
      if (!window.confirm('Are you sure you want to delete this test?')) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/recorded-class-tests/${testId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          showNotification('Test deleted successfully');
          fetchWeekContent(weekId);
        } else {
          showNotification('Failed to delete test', 'error');
        }
      } catch (error) {
        console.error('Error deleting test:', error);
        showNotification('Error deleting test', 'error');
      }
    };
  
    // Initialize form for editing
    const startEditingWeek = (week) => {
      setWeekForm({ 
        title: week.title, 
        week_number: week.week_number 
      });
      setEditingWeek(week.id);
    };
  
    const startEditingVideo = (video, weekId) => {
      setVideoForm({
        title: video.title,
        video_link: video.video_link, // Match DB field
        week_id: weekId
      });
      setEditingVideo(video.id);
    };
  
    const startEditingNote = (note, weekId) => {
      setNoteForm({
        title: note.title,
        file_link: note.file_link, // Match DB field
        week_id: weekId
      });
      setEditingNote(note.id);
    };
  
    const startEditingTest = (test, weekId) => {
      setTestForm({
        title: test.title,
        test_id: test.test_id,
        week_id: weekId,
        start_time: formatForDateTimeInput(test.start_time),
        end_time: formatForDateTimeInput(test.end_time)
      });
      setEditingTest(test.id);
    };

  // Format date helper
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-lg ${
          notification.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        } z-50 transition-opacity duration-300`}>
          {notification.message}
        </div>
      )}
      
      <div className="px-4 md:px-6">
        
        
        {loading && !course ? (
          <div className="bg-white p-8 rounded-md shadow-sm text-center">
            <p className="text-gray-500">Loading course details...</p>
          </div>
        ) : course ? (
          <>
            {/* Course Details Header */}
            <div className="bg-white p-6 rounded-md shadow-sm mb-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{course.title}</h1>
                  <p className="text-gray-600 mt-1">{course.description}</p>
                </div>
                {course.thumbnail && (
                  <img 
                    src={course.thumbnail} 
                    alt={course.title} 
                    className="mt-3 md:mt-0 h-24 rounded-md object-cover"
                  />
                )}
              </div>
              
              <div className="flex flex-wrap gap-4 border-t border-gray-100 pt-4">
                <div className="bg-indigo-50 px-3 py-2 rounded-md">
                  <span className="text-sm text-gray-600">ID:</span>
                  <span className="ml-2 font-medium">{course.content_id}</span>
                </div>
                <div className="bg-green-50 px-3 py-2 rounded-md">
                  <span className="text-sm text-gray-600">Sell Price:</span>
                  <span className="ml-2 font-medium">₹{parseFloat(course.sell_price).toLocaleString()}</span>
                </div>
                <div className="bg-blue-50 px-3 py-2 rounded-md">
                  <span className="text-sm text-gray-600">Max Price:</span>
                  <span className="ml-2 font-medium">₹{parseFloat(course.max_price).toLocaleString()}</span>
                </div>
                <div className="bg-purple-50 px-3 py-2 rounded-md flex items-center">
                  <span className="text-sm text-gray-600">Access Until:</span>
                  <span className="ml-2 font-medium">{formatDate(course.access_end_date)}</span>
                </div>
              </div>
            </div>
            
            {/* Weeks Section */}
            <div className="mb-6">
              <div className="mb-4 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">Course Content</h2>
                <button 
                  onClick={() => {
                    setShowWeekForm(true);
                    setEditingWeek(null);
                    setWeekForm({ title: '', week_number: '' });
                  }}
                  className="flex items-center bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                >
                  <PlusCircle size={18} className="mr-2" />
                  Add Week
                </button>
              </div>
              
              {/* Add/Edit Week Form */}
              {(showWeekForm || editingWeek) && (
                <div className="bg-white p-6 rounded-md shadow-sm mb-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-4">
                    {editingWeek ? 'Edit Week' : 'Add New Week'}
                  </h3>
                  <form onSubmit={editingWeek ? handleUpdateWeek : handleAddWeek}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Week Title
                        </label>
                        <input
                          type="text"
                          required
                          value={weekForm.title}
                          onChange={(e) => setWeekForm({...weekForm, title: e.target.value})}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                          placeholder="e.g. Introduction to Basics"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Week Number
                        </label>
                        <input
                          type="number"
                          required
                          min="1"
                          value={weekForm.week_number}
                          onChange={(e) => setWeekForm({...weekForm, week_number: e.target.value})}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                          placeholder="e.g. 1"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button 
                        type="button"
                        onClick={() => {
                          setShowWeekForm(false);
                          setEditingWeek(null);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                      >
                        <Save size={16} className="mr-2" />
                        {editingWeek ? 'Update Week' : 'Save Week'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
              
              {/* Weeks List */}
              {weeks.length === 0 ? (
                <div className="bg-white p-6 rounded-md shadow-sm text-center">
                  <p className="text-gray-500">No weeks added yet. Add your first week to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {weeks.map(week => (
                    <div key={week.id} className="bg-white rounded-md shadow-sm overflow-hidden">
                      {/* Week Header */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between bg-gray-50"
                        onClick={() => toggleWeek(week.id)}
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mr-3">
                            <Calendar size={18} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800">
                              Week {week.week_number}: {week.title}
                            </h3>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditingWeek(week);
                            }}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full mr-2"
                            title="Edit week"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteWeek(week.id);
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-full mr-2"
                            title="Delete week"
                          >
                            <Trash2 size={16} />
                          </button>
                          {expandedWeeks[week.id] ? (
                            <ChevronUp className="text-gray-500" size={20} />
                          ) : (
                            <ChevronDown className="text-gray-500" size={20} />
                          )}
                        </div>
                      </div>

                      {/* Week Content */}
                      {expandedWeeks[week.id] && (
                        <div className="p-4 border-t border-gray-100">
                          {!weekContent[week.id] ? (
                            <p className="text-gray-500 py-4 text-center">Loading content...</p>
                          ) : (
                            <div className="space-y-6">
                              {/* Videos Section */}
                              <div>
  <div className="flex justify-between items-center mb-2">
    <h4 className="text-sm font-semibold text-gray-600 flex items-center">
      <Film size={16} className="mr-2" /> RECORDED CLASSES
    </h4>
    <button 
      onClick={() => {
        setVideoForm({ title: '', video_link: '', week_id: week.id });
        setEditingVideo('new');
      }}
      className="flex items-center text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
    >
      <PlusCircle size={14} className="mr-1" />
      Add Video
    </button>
  </div>

  {/* Add Video Form */}
  {editingVideo === 'new' && videoForm.week_id === week.id && (
    <div className="bg-blue-50 p-3 rounded-md mb-3">
      <form onSubmit={(e) => handleAddVideo(e, week.id)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Video Title
            </label>
            <input
              type="text"
              required
              value={videoForm.title}
              onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
              className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
              placeholder="e.g. Introduction to Algebra"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Video Link
            </label>
            <input
              type="url"
              required
              value={videoForm.video_link}
              onChange={(e) => setVideoForm({ ...videoForm, video_link: e.target.value })}
              className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
              placeholder="https://youtube.com/embed/example"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button 
            type="button"
            onClick={() => setEditingVideo(null)}
            className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            type="submit"
            className="flex items-center px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Save size={12} className="mr-1" />
            Save Video
          </button>
        </div>
      </form>
    </div>
  )}

  {/* Edit Video Form */}
  {editingVideo !== null && editingVideo !== 'new' && videoForm.week_id === week.id && (
    <div className="bg-blue-50 p-3 rounded-md mb-3">
      <form onSubmit={(e) => handleUpdateVideo(e, week.id)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Video Title
            </label>
            <input
              type="text"
              required
              value={videoForm.title}
              onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
              className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Video Link
            </label>
            <input
              type="url"
              required
              value={videoForm.video_link}
              onChange={(e) => setVideoForm({ ...videoForm, video_link: e.target.value })}
              className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button 
            type="button"
            onClick={() => setEditingVideo(null)}
            className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            type="submit"
            className="flex items-center px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Save size={12} className="mr-1" />
            Update Video
          </button>
        </div>
      </form>
    </div>
  )}

  {/* Videos List */}
  <div className="space-y-2">
    {weekContent[week.id].videos.length === 0 ? (
      <p className="text-sm text-gray-500 italic">No videos added yet</p>
    ) : (
      weekContent[week.id].videos.map((video) => (
        <div key={video.id} className="bg-gray-50 p-3 rounded flex justify-between items-center">
          <div className="flex items-center">
            <Film size={16} className="text-blue-600 mr-2" />
            <span className="text-gray-800">{video.title}</span>
          </div>
          <div className="flex space-x-1">
            <a
              href={video.video_link}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title="View video"
            >
              <ExternalLink size={14} />
            </a>
            <button 
              onClick={() => startEditingVideo(video, week.id)}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title="Edit video"
            >
              <Edit2 size={14} />
            </button>
            <button 
              onClick={() => handleDeleteVideo(video.id, week.id)}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
              title="Delete video"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))
    )}
  </div>
</div>

                              
                              {/* Notes Section */}
                              <div>
  <div className="flex justify-between items-center mb-2">
    <h4 className="text-sm font-semibold text-gray-600 flex items-center">
      <FileText size={16} className="mr-2" /> NOTES
    </h4>
    <button 
      onClick={() => {
        setNoteForm({ title: '', file_link: '', week_id: week.id });
        setEditingNote('new');
      }}
      className="flex items-center text-xs px-2 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100 transition-colors"
    >
      <PlusCircle size={14} className="mr-1" />
      Add Notes
    </button>
  </div>

  {/* Note Form */}
  {editingNote === 'new' && noteForm.week_id === week.id && (
  <div className="bg-green-50 p-3 rounded-md mb-3">
    <form onSubmit={(e) => handleAddNote(e, week.id)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Note Title
          </label>
          <input
            type="text"
            required
            value={noteForm.title}
            onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
            className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
            placeholder="e.g. Week 1 Notes"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Upload PDF File
          </label>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setNoteForm({ ...noteForm, file: e.target.files[0] })}
            className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button 
          type="button"
          onClick={() => setEditingNote(null)}
          className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button 
          type="submit"
          className="flex items-center px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          <Save size={12} className="mr-1" />
          Save Note
        </button>
      </div>
    </form>
  </div>
)}
{editingNote && editingNote !== 'new' && noteForm.week_id === week.id && (
  <div className="bg-green-50 p-3 rounded-md mb-3">
    <form onSubmit={(e) => handleUpdateNote(e, week.id)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Note Title
          </label>
          <input
            type="text"
            required
            value={noteForm.title}
            onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
            className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Upload New PDF File
          </label>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setNoteForm({ ...noteForm, file: e.target.files[0] })}
            className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
          />
          {noteForm.file_link && (
            <a
              href={noteForm.file_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 mt-1 inline-block"
            >
              View existing file
            </a>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button 
          type="button"
          onClick={() => setEditingNote(null)}
          className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button 
          type="submit"
          className="flex items-center px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          <Save size={12} className="mr-1" />
          Update Note
        </button>
      </div>
    </form>
  </div>
)}


  {/* Notes List */}
  <div className="space-y-2">
    {weekContent[week.id].notes.length === 0 ? (
      <p className="text-sm text-gray-500 italic">No notes added yet</p>
    ) : (
      weekContent[week.id].notes.map(note => (
        <div
          key={note.id}
          className="bg-gray-50 p-3 rounded flex justify-between items-center"
        >
          <div className="flex items-center">
            <FileText size={16} className="text-green-600 mr-2" />
            <span className="text-gray-800">{note.title}</span>
          </div>
          <div className="flex space-x-1">
            <a
              href={note.file_link}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title="View note"
            >
              <ExternalLink size={14} />
            </a>
            <button
              onClick={() => startEditingNote(note, week.id)}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title="Edit note"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => handleDeleteNote(note.id, week.id)}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
              title="Delete note"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))
    )}
  </div>
</div>

                              
                              {/* Tests Section */}
                              <div>
  <div className="flex justify-between items-center mb-2">
    <h4 className="text-sm font-semibold text-gray-600 flex items-center">
      <CheckSquare size={16} className="mr-2" /> TESTS
    </h4>
    <button
      onClick={() => {
        setTestForm({ title: '', test_id: '', week_id: week.id });
        setEditingTest('new');
      }}
      className="flex items-center text-xs px-2 py-1 bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors"
    >
      <PlusCircle size={14} className="mr-1" />
      Add Test
    </button>
  </div>

  {/* Add Test Form */}
  {(editingTest === 'new' || (editingTest && editingTest !== 'new')) && testForm.week_id === week.id && (
  <div className="bg-purple-50 p-3 rounded-md mb-3">
    <form onSubmit={(e) => editingTest === 'new' ? handleAddTest(e, week.id) : handleUpdateTest(e, week.id)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">

        {/* Select Test */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Select Test
          </label>
          <select
            required
            value={testForm.test_id}
            onChange={(e) => {
              const selectedTestId = Number(e.target.value);
              const selected = availableTests.find(t => t.test_id === selectedTestId);
              setTestForm(prev => ({
                ...prev,
                test_id: selectedTestId,
                title: useOriginalTitle
                  ? (selected?.name || `Course Test ${selectedTestId}`)
                  : prev.title
              }));
            }}
            className="w-full p-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="">Select a test</option>
            {availableTests.map(test => (
              <option key={test.test_id} value={test.test_id}>
                {test.name} (ID: {test.test_id})
              </option>
            ))}
          </select>
        </div>

        {/* Test Title + Original/Custom Radio Toggle */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Test Title *
          </label>
          <input
            type="text"
            value={testForm.title}
            onChange={(e) => {
              setTestForm(prev => ({ ...prev, title: e.target.value }));
              setUseOriginalTitle(false);
            }}
            disabled={useOriginalTitle}
            required
            className="w-full p-1.5 text-sm border border-gray-300 rounded-md bg-white"
            placeholder="Enter custom title"
          />
          {useOriginalTitle && (
            <p className="text-xs text-gray-500 mt-1">Using the original test name</p>
          )}

          {/* Radio Buttons */}
          <div className="flex gap-4 mt-2 text-sm">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name={`title-type-${week.id}`}
                checked={useOriginalTitle}
                onChange={() => {
                  const selected = availableTests.find(t => t.test_id === Number(testForm.test_id));
                  setUseOriginalTitle(true);
                  setTestForm(prev => ({
                    ...prev,
                    title: selected?.name || `Course Test ${prev.test_id}`
                  }));
                }}
              />
              Original Name
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name={`title-type-${week.id}`}
                checked={!useOriginalTitle}
                onChange={() => setUseOriginalTitle(false)}
              />
              Custom Name
            </label>
          </div>
        </div>

        {/* Time Window (Optional) */}
        <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Start Time (Optional)
            </label>
            <input
              type="datetime-local"
              value={testForm.start_time}
              onChange={(e) => setTestForm(prev => ({ ...prev, start_time: e.target.value }))}
              className="w-full p-1.5 text-xs border border-gray-300 rounded-md bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              End Time (Optional)
            </label>
            <input
              type="datetime-local"
              value={testForm.end_time}
              onChange={(e) => setTestForm(prev => ({ ...prev, end_time: e.target.value }))}
              className="w-full p-1.5 text-xs border border-gray-300 rounded-md bg-white"
            />
          </div>
          <div className="col-span-2 text-[11px] text-gray-500">
            Leave both empty for unrestricted access.
          </div>
        </div>

      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <button 
          type="button"
          onClick={() => setEditingTest(null)}
          className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button 
          type="submit"
          className="flex items-center px-3 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700"
        >
          <Save size={12} className="mr-1" />
          {editingTest === 'new' ? 'Save Test' : 'Update Test'}
        </button>
      </div>
    </form>
  </div>
)}


  {/* Tests List */}
  <div className="space-y-2">
    {weekContent[week.id].tests.length === 0 ? (
      <p className="text-sm text-gray-500 italic">No tests added yet</p>
    ) : (
      weekContent[week.id].tests.map(test => (
        <div key={test.id} className="bg-gray-50 p-3 rounded flex justify-between items-center">
          <div className="flex items-center">
            <CheckSquare size={16} className="text-purple-600 mr-2" />
            <span className="text-gray-800">{test.title}</span>
            {test.test_id && (
              <span className="ml-2 text-xs text-gray-500">
                (test_id: {test.test_id})
              </span>
            )}
          </div>
          <div className="flex space-x-1">
            <a
              href={`/tests/${test.test_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title="View Test"
            >
              <Eye size={14} />
            </a>
            <button
              onClick={() => startEditingTest(test, week.id)}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title="Edit test"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => handleDeleteTest(test.id, week.id)}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
              title="Delete test"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))
    )}
  </div>
</div>

                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="bg-white p-8 rounded-md shadow-sm text-center">
            <p className="text-gray-500">Course not found. Please check the course ID.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseAdminDashboard;